<template>
  <div id="app" :class="{ 'mocked-mode': isMockedMode }">
    <!-- Only over the open panel; the loader would overflow the 48px closed bubble. -->
    <Loading v-if="showCustomisationLoader && showRouterView" is-loading full center @fail="handleCustomisationRetry" />
    <template v-else>
      <!-- Wait for customisation before showing the bubble, else its icon flashes unstyled. -->
      <FloatButton v-show="showFloatButton && !showCustomisationLoader" />
      <div v-show="!showFloatButton" class="app-content">
        <!-- MyRequestComponent and Article componenet will show/close by EventBus -->
        <MyRequestComponent v-if="showMyRequest" />
        <Article
          v-if="showArticle"
          :article-id="showArticleId"
          :title="articleTitle"
          :category-name="categoryName"
          :body="articleBody"
          :section-title="articleSectionTitle"
          :fetch-article="fetchArticle"
          :is-from-autocomplete="isFromAutocomplete"
          :use-config="articleUseConfig"
        />
        <router-view v-if="showRouterView" v-show="!showMyRequest && !showArticle" />
      </div>

      <!-- Chat Widget - Visible on all pages -->
      <ChatWidget v-if="showChatWidget && !showCustomisationLoader && !showFloatButton" />
    </template>

    <!-- Mocked Mode Overlay - Blocks all interactions -->
    <div v-if="isMockedMode" class="mocked-mode-overlay" />
  </div>
</template>

<script setup lang="ts">
import { ConnectToAgent } from '@/common/bot/types';
import MyRequestComponent from '@/components/MyRequest/index.vue';
import Article from '@/components/Article.vue';
import FloatButton from '@/components/FloatButton.vue';
import Loading from '@/components/loading/loading.vue';
import ChatWidget from '@/views/ChatWidget/ChatWidget.vue';

defineProps<{
  isMockedMode: boolean;
  showCustomisationLoader: boolean;
  showFloatButton: boolean;
  showMyRequest: boolean;
  showArticle: boolean;
  showArticleId: string;
  articleTitle: string;
  categoryName: string;
  articleBody: string;
  articleSectionTitle: string;
  fetchArticle: boolean;
  isFromAutocomplete: boolean;
  articleUseConfig: ConnectToAgent;
  showRouterView: boolean;
  showChatWidget: boolean;
}>();

const emit = defineEmits<{
  (e: 'handleCustomisationRetry'): void;
  (e: 'handleCustomisationUpdate', data: Record<string, unknown>): void;
}>();

const handleCustomisationRetry = () => {
  emit('handleCustomisationRetry');
};
</script>

<style lang="scss">
@import '@/assets/scss/common.scss';

.app-content {
  height: 100vh;
}

// Mocked mode styles - blocks all interactions
.mocked-mode-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  cursor: not-allowed;
  // Transparent overlay that captures all clicks
  background: transparent;
}

// Optional: Add visual indication that interactions are disabled
#app.mocked-mode {
  // Disable text selection in mocked mode
  user-select: none;
}
</style>
